/**
 * Tastatur und Eingabefelder in der nativen App (Oktober 2026).
 *
 * Problem: Beim Antippen eines Feldes klappt die Tastatur auf und verdeckt
 * das Feld teilweise — man sieht nicht, was man tippt. Bis Oktober 2026 gab
 * es dafuer nur eine Android-Loesung in App.js; fuer iOS stand dort
 * "iOS scrollt selbst". Das stimmt mit unserer Konfiguration nicht:
 *
 * - Das Capacitor-Tastatur-Plugin meldet die WebView beim Tastatur-System ab
 *   (Keyboard.m: removeObserver:self.webView). WebKit erfaehrt also nichts von
 *   der Tastatur und schiebt das Feld nicht nach oben.
 * - Mit resize: 'native' verkleinert das Plugin die WebView erst
 *   Animationsdauer + 0,2 s NACH keyboardWillShow, also auch nach
 *   keyboardDidShow. Wer beim Ereignis misst, sieht noch die volle Hoehe und
 *   haelt ein verdecktes Feld fuer sichtbar.
 *
 * Deshalb rechnet revealFocusedField die gemeldete Tastaturhoehe selbst ein
 * (Hoehe bei geschlossener Tastatur minus Tastatur) und installKeyboardReveal
 * misst nach jedem Verkleinern der WebView noch einmal nach. Wechselt man bei
 * offener Tastatur in ein anderes Feld, kommt auf Android gar kein
 * Tastatur-Ereignis — dafuer der focusin-Listener.
 *
 * Felder mit eigener Scroll-Logik (Chat) tragen data-keyboard-scroll="off".
 */

const NON_TEXT_INPUT_TYPES = new Set([
  'button', 'checkbox', 'color', 'file', 'hidden', 'image', 'radio', 'range', 'reset', 'submit',
]);

/** Abstand, den ein Feld mindestens zur Tastatur bzw. Aktions-Leiste haben soll (px). */
const MARGIN = 16;

/** Oeffnet dieses Element die Bildschirmtastatur? */
export const isTextEntry = (el) => {
  if (!el || typeof el.matches !== 'function') return false;
  if (el.matches('textarea, select, [contenteditable="true"]')) return true;
  if (!el.matches('input')) return false;
  return !NON_TEXT_INPUT_TYPES.has(String(el.type || 'text').toLowerCase());
};

/**
 * Unterkante des Bereichs, in dem ein Feld sichtbar ist (px ab Oberkante).
 *
 * - viewport: aktuelle Hoehe der WebView (nach dem Verkleinern ohne Tastatur)
 * - baselineHeight - keyboardHeight: dasselbe, solange die WebView noch nicht
 *   verkleinert ist (iOS, die ersten ~0,45 s)
 * - die Aktions-Leiste (SafeAreaFooter) sitzt bei offener Tastatur direkt
 *   darueber und verdeckt ihren eigenen Streifen
 */
export const visibleBottom = ({ win, doc, keyboardHeight = 0, baselineHeight = 0 }) => {
  let bottom = (win.visualViewport && win.visualViewport.height) || win.innerHeight;
  if (keyboardHeight > 0 && baselineHeight > keyboardHeight) {
    bottom = Math.min(bottom, baselineHeight - keyboardHeight);
  }
  const footer = doc.querySelector('[data-safe-area-footer]');
  if (footer) {
    const footerHeight = footer.getBoundingClientRect().height;
    if (footerHeight > 0) bottom -= footerHeight;
  }
  return bottom;
};

/**
 * Holt das fokussierte Feld in die Mitte des sichtbaren Bereichs, wenn es
 * verdeckt ist. Gibt zurueck, ob gescrollt wurde.
 */
export const revealFocusedField = ({
  win = window,
  doc = document,
  keyboardHeight = 0,
  baselineHeight = 0,
} = {}) => {
  const el = doc.activeElement;
  if (!isTextEntry(el)) return false;
  if (el.closest('[data-keyboard-scroll="off"]')) return false;

  const bottom = visibleBottom({ win, doc, keyboardHeight, baselineHeight });
  const rect = el.getBoundingClientRect();
  if (rect.bottom <= bottom - MARGIN && rect.top >= 0) return false;

  // Ein hohes Textfeld passt nicht in die Mitte — dann zaehlt sein Anfang,
  // dort steht beim Antippen meist der Cursor.
  const block = rect.height > bottom / 2 ? 'start' : 'center';
  const reduceMotion = typeof win.matchMedia === 'function'
    && win.matchMedia('(prefers-reduced-motion: reduce)').matches;
  el.scrollIntoView({ block, behavior: reduceMotion ? 'auto' : 'smooth' });
  return true;
};

/**
 * Verdrahtet Tastatur-Ereignisse, Groessenaenderung und Feldwechsel.
 * Setzt ausserdem body.keyboard-open (blendet die Bottom-Navigation aus,
 * siehe index.css) und die Hoehe der WebView OHNE Tastatur als
 * --app-height-without-keyboard auf <html> (Klasse has-keyboard-baseline).
 * Daran haengen Groessen, die beim Tippen nicht mitschrumpfen sollen — z.B.
 * die Profilkarten in der Suche (.profile-card-height). Gibt eine
 * Aufraeum-Funktion zurueck.
 *
 * @param {object} options
 * @param {object} options.Keyboard  Plugin aus @capacitor/keyboard
 * @param {string} options.platform  Capacitor.getPlatform()
 */
export const installKeyboardReveal = ({ Keyboard, platform, win = window, doc = document }) => {
  const isIOS = platform === 'ios';
  // iOS: willShow kommt vor der Animation (Navigation sofort weg),
  // Android: didShow, wenn die WebView schon verkleinert ist.
  const showEvent = isIOS ? 'keyboardWillShow' : 'keyboardDidShow';
  const hideEvent = isIOS ? 'keyboardWillHide' : 'keyboardDidHide';

  let open = false;
  let keyboardHeight = 0;
  let baselineHeight = win.innerHeight;
  let baselineWidth = win.innerWidth;
  let timers = [];
  let disposed = false;
  const handles = [];

  // Seit Oktober 2026: In der Suche wurden die Profilkarten beim Tippen
  // zusammengequetscht. Ihre Hoehe hing an 100svh, und das ist in der App die
  // Hoehe der WebView — die schrumpft mit resize: 'native' um die Tastatur.
  const root = doc.documentElement;
  const publishBaseline = () => {
    const value = `${Math.round(baselineHeight)}px`;
    if (root.style.getPropertyValue('--app-height-without-keyboard') !== value) {
      root.style.setProperty('--app-height-without-keyboard', value);
    }
  };
  publishBaseline();
  root.classList.add('has-keyboard-baseline');

  const clearTimers = () => {
    timers.forEach((t) => clearTimeout(t));
    timers = [];
  };
  const reveal = () => {
    if (!open) return;
    revealFocusedField({ win, doc, keyboardHeight, baselineHeight });
  };
  // Neue Messung ersetzt alle noch ausstehenden — sonst springt die Seite mehrfach.
  const schedule = (delays) => {
    clearTimers();
    timers = delays.map((delay) => setTimeout(reveal, delay));
  };

  const onShow = (info) => {
    open = true;
    keyboardHeight = (info && info.keyboardHeight) || 0;
    doc.body.classList.add('keyboard-open');
    // iOS: einmal sofort (Tastaturhoehe eingerechnet), einmal nach dem
    // Verkleinern der WebView (Animation ~0,25 s + 0,2 s Plugin-Verzoegerung).
    schedule(isIOS ? [50, 600] : [0, 300]);
  };
  const onHide = () => {
    open = false;
    keyboardHeight = 0;
    clearTimers();
    doc.body.classList.remove('keyboard-open');
  };
  const onResize = () => {
    // Hoehe der WebView ohne Tastatur nachfuehren. Sie darf nur WACHSEN:
    // Android verkleinert die WebView schon vor keyboardDidShow, also zu einem
    // Zeitpunkt, an dem hier noch "geschlossen" gilt. Zu hoch geschaetzt ist
    // harmlos (dann entscheidet die echte Hoehe), zu niedrig liesse jedes Feld
    // unter der Mitte springen. Neu gesetzt wird sie nur, wenn sich die Breite
    // aendert (Geraet gedreht).
    if (!open) {
      if (win.innerWidth !== baselineWidth) {
        baselineWidth = win.innerWidth;
        baselineHeight = win.innerHeight;
      } else {
        baselineHeight = Math.max(baselineHeight, win.innerHeight);
      }
      publishBaseline();
      return;
    }
    schedule([50]);
  };
  const onFocusIn = () => {
    if (open) schedule([isIOS ? 100 : 50]);
  };

  [[showEvent, onShow], [hideEvent, onHide]].forEach(([eventName, handler]) => {
    Promise.resolve(Keyboard.addListener(eventName, handler))
      .then((handle) => {
        if (disposed) handle?.remove?.();
        else handles.push(handle);
      })
      .catch(() => {});
  });
  win.addEventListener('resize', onResize);
  if (win.visualViewport) win.visualViewport.addEventListener('resize', onResize);
  doc.addEventListener('focusin', onFocusIn);

  return () => {
    disposed = true;
    clearTimers();
    handles.forEach((handle) => handle?.remove?.());
    win.removeEventListener('resize', onResize);
    if (win.visualViewport) win.visualViewport.removeEventListener('resize', onResize);
    doc.removeEventListener('focusin', onFocusIn);
    doc.body.classList.remove('keyboard-open');
    root.classList.remove('has-keyboard-baseline');
    root.style.removeProperty('--app-height-without-keyboard');
  };
};
